/**
 * SigningEngineRoutingPage — Workflow · Signing Engine Routing.
 *
 * The Q3 kill-switch: which signing engine (Internal / SignNow / Wet-attested)
 * handles each document, for each customer type (cohort), on each surface. The
 * orchestrator (SigningEngineRoutingService.ChooseEngineAsync) reads this table
 * at signing time — exact (cohort, doc, surface) match first, then the surface
 * wildcard '*', then the safe fallback 'signnow'. Flip a row and the next
 * signing event picks it up; no deploy. Risk Mgmt sign-off required for any
 * Phase-1 production change. Every mutation stamps a SessionEvent audit row.
 *
 * Contract (verified against the backend 2026-06-16):
 *   - Read:   client.retail.admin.signingEngineRouting.list()
 *             → GET /v3/admin/signing-engine-routing → { success, data: rows }
 *   - Upsert: client.retail.admin.signingEngineRouting.upsert(body)
 *             → POST /v3/admin/signing-engine-routing/upsert (matches existing
 *               by natural key cohort+doc+surface; validates engine + surface)
 *   - Delete: client.retail.admin.signingEngineRouting.remove(id)
 *             → DELETE /v3/admin/signing-engine-routing/{id:long}
 *
 * Migrated 2026-06-16 from ROP-teams Admin — the prior Slot-C violation (admin
 * config wrongly registered in the operational app). The port also FIXES three
 * FE-only field-name bugs in the source editor: it read `routingId` /
 * `effectiveFrom` / `modifiedBy`-`modifiedAt`, but the wire returns the entity
 * shape `id` / `effectiveDate` / `updatedBy`-`updatedAt`. The mismatches broke
 * Delete (DELETE …/undefined), the effective-date column/field, and the audit
 * column. This page uses the real wire fields, so all three work. Backend
 * unchanged.
 *
 * Auth: the generic admin surface relies on the ambient MSAL bearer (no
 * bypass-key in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

type Engine = 'internal' | 'signnow' | 'wet-attested';
type Surface = '*' | 'in-branch' | 'online-cart' | 'rcp-remote' | 'wet-attested';

// Real wire shape (raw SigningEngineRouting entity, camelCased).
interface Row {
  id: number;
  cohortKey: string;
  docTypeCode: string;
  surface: Surface;
  engine: Engine;
  effectiveDate?: string | null;
  reason?: string | null;
  isActive: boolean;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

interface EditDraft {
  id?: number;
  cohortKey: string;
  docTypeCode: string;
  surface: Surface;
  engine: Engine;
  effectiveDate: string;
  reason: string;
  isActive: boolean;
  isNew: boolean;
}

const COHORT_LABELS: Record<string, string> = {
  personal: 'Personal',
  'trust-revocable': 'Revocable Trust',
  'trust-irrevocable': 'Irrevocable Trust',
  'trust-self-settled-snt': 'Self-Settled SNT',
  'trust-revocable-deceased-successor': 'Revocable Trust — Successor',
  'business-llc': 'LLC',
  'business-corporation': 'Corporation',
  'business-partnership': 'Partnership',
  'business-sole-prop': 'Sole Proprietorship',
  estate: 'Estate',
  ira: 'IRA',
};

const DOC_TYPE_LABELS: Record<string, string> = {
  TRUSTEE_CERT: 'Trustee Certificate',
  ESIGN_CONSENT: 'ESIGN Consent',
  SIG_CARD: 'Signature Card',
  BO_CERT: 'Beneficial Owner Certification',
  BANKING_RESOLUTION: 'Banking Resolution',
  REG_E_OPT_IN: 'Regulation E Opt-In',
  PRIVACY_NOTICE: 'Privacy Notice',
  DEP_AGREE: 'Deposit Agreement',
  W9: 'W-9',
  W8BEN: 'W-8BEN',
  IRA_DISCLOSURE: 'IRA Disclosure',
  TRUST_AGREEMENT_SCAN: 'Trust Agreement (Scan)',
};

const SURFACE_LABELS: Record<Surface, string> = {
  '*': 'All surfaces (any)',
  'in-branch': 'In-branch attended',
  'online-cart': 'Online cart (embedded)',
  'rcp-remote': 'RCP remote portal',
  'wet-attested': 'Wet-sign + FSR attestation',
};

const ENGINE_LABEL: Record<Engine, string> = {
  internal: '🟦 Internal',
  signnow: '🟧 SignNow',
  'wet-attested': '📝 Wet-attested',
};

const ENGINE_TONE: Record<Engine, 'info' | 'warn' | 'muted'> = {
  internal: 'info',
  signnow: 'warn',
  'wet-attested': 'muted',
};

const SURFACES: ReadonlyArray<Surface> = ['*', 'in-branch', 'online-cart', 'rcp-remote', 'wet-attested'];
const ENGINES: ReadonlyArray<Engine> = ['internal', 'signnow', 'wet-attested'];

const COMMON_MISTAKES: ReadonlyArray<string> = [
  'Flipping a cohort engine without Risk Mgmt sign-off — Risk approval is required for every Phase-1 route change.',
  "Setting engine = Internal on Irrevocable Trust or Self-Settled SNT — these are locked to SignNow per Q10.",
  'Deleting a row instead of flipping IsActive — keep the audit history; mark inactive to retire.',
  'Choosing Wet-attested for a doc with no Q8 wet-sign FSR attestation flow wired — the FSR hits a dead end.',
  "Letting the wildcard surface '*' contradict a more specific row — the orchestrator picks the most specific match first.",
];

const REG_CITATIONS: ReadonlyArray<string> = [
  'ESIGN Act — 15 USC §7001 (electronic-signature equivalency)',
  'Missouri RSMo §432.230 (state electronic-record equivalency)',
  'OCC Bulletin 2013-29 (third-party risk management)',
];

function cohortLabel(k: string): string {
  return COHORT_LABELS[k] ?? k;
}
function docTypeLabel(c: string): string {
  return DOC_TYPE_LABELS[c] ?? c;
}
function nextEngine(e: Engine): Engine {
  return ENGINES[(ENGINES.indexOf(e) + 1) % ENGINES.length];
}

function emptyDraft(): EditDraft {
  return {
    cohortKey: '',
    docTypeCode: '',
    surface: '*',
    engine: 'signnow',
    effectiveDate: '',
    reason: '',
    isActive: true,
    isNew: true,
  };
}

export function SigningEngineRoutingPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cohortFilter, setCohortFilter] = useState('');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = (await client.retail.admin.signingEngineRouting.list()) as unknown as {
          success: boolean;
          data?: ReadonlyArray<Row>;
          error?: string;
        };
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) setRows(resp.data);
        else setLoadError(resp.error ?? "Couldn't load the signing-engine routing table.");
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const cohortOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) set.add(r.cohortKey);
    return [
      { value: '', label: 'All customer types' },
      ...Array.from(set).sort().map((c) => ({ value: c, label: cohortLabel(c) })),
    ];
  }, [rows]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (cohortFilter && r.cohortKey !== cohortFilter) return false;
      if (needle) {
        const hay = `${r.cohortKey} ${cohortLabel(r.cohortKey)} ${r.docTypeCode} ${docTypeLabel(r.docTypeCode)} ${r.surface} ${r.engine} ${r.reason ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, cohortFilter, search]);

  const upsert = useCallback(
    async (body: {
      cohortKey: string;
      docTypeCode: string;
      surface: Surface;
      engine: Engine;
      effectiveDate?: string;
      reason?: string;
      isActive: boolean;
    }): Promise<boolean> => {
      const resp = (await client.retail.admin.signingEngineRouting.upsert(body)) as unknown as {
        success: boolean;
        error?: string;
      };
      if (!resp.success) throw new Error(resp.error ?? "Couldn't save the routing change.");
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.cohortKey.trim() || !draft.docTypeCode.trim()) {
      setSaveError('Customer type and document type are both required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert({
        cohortKey: draft.cohortKey.trim(),
        docTypeCode: draft.docTypeCode.trim(),
        surface: draft.surface,
        engine: draft.engine,
        ...(draft.effectiveDate ? { effectiveDate: new Date(`${draft.effectiveDate}T00:00:00Z`).toISOString() } : {}),
        ...(draft.reason.trim() ? { reason: draft.reason.trim() } : {}),
        isActive: draft.isActive,
      });
      setDraft(null);
      setToast({
        tone: 'info',
        text: `Routing saved — ${cohortLabel(draft.cohortKey)} · ${docTypeLabel(draft.docTypeCode)} now uses ${ENGINE_LABEL[draft.engine]}.`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, upsert, refresh]);

  const handleFlip = useCallback(
    async (r: Row) => {
      const next = nextEngine(r.engine);
      setBusyId(r.id);
      try {
        await upsert({
          cohortKey: r.cohortKey,
          docTypeCode: r.docTypeCode,
          surface: r.surface,
          engine: next,
          reason: `Quick-flip from ${r.engine} to ${next}`,
          isActive: r.isActive,
        });
        setToast({ tone: 'info', text: `${cohortLabel(r.cohortKey)} · ${docTypeLabel(r.docTypeCode)} → ${ENGINE_LABEL[next]}.` });
        refresh();
      } catch (e) {
        setToast({ tone: 'warn', text: `Flip failed: ${e instanceof Error ? e.message : String(e)}` });
      } finally {
        setBusyId(null);
      }
    },
    [upsert, refresh],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = (await client.retail.admin.signingEngineRouting.remove(String(deleteTarget.id))) as unknown as {
        success: boolean;
        error?: string;
      };
      if (!resp.success) throw new Error(resp.error ?? 'Delete failed.');
      setToast({
        tone: 'warn',
        text: `Removed — ${cohortLabel(deleteTarget.cohortKey)} · ${docTypeLabel(deleteTarget.docTypeCode)} falls back to the default engine (SignNow).`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<Row>> = useMemo(
    () => [
      { header: 'Customer type', cell: (r) => <span className="cell-strong">{cohortLabel(r.cohortKey)}</span> },
      { header: 'Document type', cell: (r) => <span>{docTypeLabel(r.docTypeCode)}</span> },
      { header: 'Surface', cell: (r) => <span className="cell-desc">{SURFACE_LABELS[r.surface] ?? r.surface}</span> },
      { header: 'Engine', cell: (r) => <Pill tone={ENGINE_TONE[r.engine]}>{ENGINE_LABEL[r.engine]}</Pill> },
      {
        header: 'Effective',
        cell: (r) => <span className="cell-desc">{r.effectiveDate ? r.effectiveDate.slice(0, 10) : '—'}</span>,
      },
      {
        header: 'Last changed by',
        cell: (r) => (
          <span className="cell-desc">
            {r.updatedBy ?? '—'}
            {r.updatedAt && <div className="cell-dim">{r.updatedAt.slice(0, 16).replace('T', ' ')}</div>}
          </span>
        ),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => void handleFlip(r)} disabled={busyId === r.id}>
              Flip to {ENGINE_LABEL[nextEngine(r.engine)]}
            </Button>
            <Button
              onClick={() => {
                setDraft({
                  id: r.id,
                  cohortKey: r.cohortKey,
                  docTypeCode: r.docTypeCode,
                  surface: r.surface,
                  engine: r.engine,
                  effectiveDate: r.effectiveDate ? r.effectiveDate.slice(0, 10) : '',
                  reason: r.reason ?? '',
                  isActive: r.isActive,
                  isNew: false,
                });
                setSaveError(null);
              }}
            >
              Edit
            </Button>
            <Button onClick={() => setDeleteTarget(r)}>Remove</Button>
          </div>
        ),
      },
    ],
    [busyId, handleFlip],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load signing-engine routing: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading signing-engine routing…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="warn">
        <strong>Risk-controlled.</strong> This is the Q3 kill-switch the signing orchestrator reads
        at signing time. Risk Mgmt sign-off is required before changing any Phase-1 row in
        production. Every change is audited (SessionEvent). Irrevocable Trust + Self-Settled SNT are
        locked to SignNow per Q10.
      </Banner>

      <Card
        title={`Signing Engine Routing — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField label="" value={cohortFilter} onChange={setCohortFilter} options={cohortOptions} />
            </div>
            <div className="users-filter">
              <TextField label="" value={search} onChange={setSearch} placeholder="cohort, doc, surface, engine, reason…" />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Routing rule
            </Button>
          </div>
        }
      >
        <DataTable<Row>
          columns={columns}
          rows={visible}
          rowKey={(r) => String(r.id)}
          emptyState={<p className="empty">No routing rules match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a routing rule' : `Edit ${cohortLabel(draft?.cohortKey ?? '')} · ${docTypeLabel(draft?.docTypeCode ?? '')}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={720}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Customer type"
                value={draft.cohortKey}
                onChange={(v) => setDraft({ ...draft, cohortKey: v.slice(0, 80) })}
                monospace
                placeholder="e.g. trust-revocable"
                hint="Cohort key (matches the cohort resolver)."
              />
              <TextField
                label="Document type"
                value={draft.docTypeCode}
                onChange={(v) => setDraft({ ...draft, docTypeCode: v.slice(0, 60).toUpperCase() })}
                monospace
                placeholder="e.g. TRUSTEE_CERT"
                hint="Doc-type code (matches the Disclosure Engine codes)."
              />
            </div>

            <div className="form-row">
              <SelectField
                label="Surface"
                value={draft.surface}
                onChange={(v) => setDraft({ ...draft, surface: v as Surface })}
                options={SURFACES.map((s) => ({ value: s, label: SURFACE_LABELS[s] }))}
                hint="'All surfaces' is the catch-all; specific surfaces override."
              />
              <SelectField
                label="Engine"
                value={draft.engine}
                onChange={(v) => setDraft({ ...draft, engine: v as Engine })}
                options={ENGINES.map((e) => ({ value: e, label: ENGINE_LABEL[e] }))}
                hint="Internal = bank-defended; SignNow = vendor-defended; Wet-attested = paper + FSR."
              />
            </div>

            <div className="form-row">
              <label className="form-field">
                <span className="form-field__label">Effective from</span>
                <input
                  type="date"
                  className="form-input"
                  aria-label="Effective from"
                  value={draft.effectiveDate}
                  onChange={(e) => setDraft({ ...draft, effectiveDate: e.target.value })}
                />
                <span className="form-field__hint">When this row starts applying. Leave blank for immediate.</span>
              </label>
              <BoolField
                label="Active"
                value={draft.isActive}
                onChange={(v) => setDraft({ ...draft, isActive: v })}
                hint="Inactive rows are ignored at signing time."
              />
            </div>

            <TextArea
              label="Reason"
              value={draft.reason}
              onChange={(v) => setDraft({ ...draft, reason: v.slice(0, 500) })}
              rows={2}
              placeholder="e.g. Phase-1 launch — internal click-to-sign for Revocable Trust in-branch per Q10 matrix."
            />

            <details className="collapsible">
              <summary className="collapsible__summary">Risk guidance — citations + common mistakes</summary>
              <div className="collapsible__body">
                <p className="form-field__label">Regulatory citations</p>
                <ul className="guidance-list">
                  {REG_CITATIONS.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                <p className="form-field__label">Common mistakes</p>
                <ul className="guidance-list">
                  {COMMON_MISTAKES.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </div>
            </details>
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Remove this routing rule?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Remove'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We'll remove the routing rule for <strong>{cohortLabel(deleteTarget.cohortKey)}</strong> ·{' '}
              <strong>{docTypeLabel(deleteTarget.docTypeCode)}</strong> on{' '}
              <strong>{SURFACE_LABELS[deleteTarget.surface] ?? deleteTarget.surface}</strong>. Future
              signings for that combination fall back to the default engine (SignNow) until a new row
              is added. Already-signed documents are unaffected.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
